import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { TransactionDetailsEntity, TransactionLineEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { DiscountType } from "@modules/finances/gateway/transactionDto"
import { TransactionStatus } from "@modules/finances/types/financesType"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { shortenText } from "@modules/utils/stringUtils"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useMemo } from "react"

type InvoiceDetailsTableProps = {
  transactionDetails: TransactionDetailsEntity
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<TransactionLineEntity>()

export function InvoiceDetailsTable({ transactionDetails, className, ...props }: InvoiceDetailsTableProps) {
  const language = useLanguage()
  const totalAmount = transactionDetails.lines.reduce((acc, line) => acc + line.totalAmount, 0)

  const footerGroup = useMemo(() => {
    const discountsPromoCode = transactionDetails.discounts.filter(
      (discount) => discount.type === DiscountType.PROMO_CODE,
    )

    const discountsCoupon = transactionDetails.discounts.filter((discount) => discount.type === DiscountType.DISCOUNT)

    if (transactionDetails.refunded) {
      return (
        <div className={cn("flex flex-col gap-2", className)} {...props}>
          {discountsPromoCode.length > 0 &&
            discountsPromoCode.map((promoCode, promoCodeIndex) => (
              <div className="flex flex-row items-end justify-end" key={promoCodeIndex}>
                <Text fontStyle="LL" className="pr-4 text-textDefault-body">
                  {promoCode.name ? word("promoCode.short", { code: shortenText(promoCode.name, 10) }) : ""}
                </Text>
                <Text fontStyle="BM" className="text-textDefault-heading">
                  {formatCurrency(
                    promoCode.couponType === CouponType.DIRECT
                      ? -promoCode.amount
                      : -(totalAmount * promoCode.amount) / 100,
                    transactionDetails.currency,
                    language,
                  )}
                </Text>
              </div>
            ))}

          {discountsCoupon.length > 0 &&
            discountsCoupon.map((discountCoupon, discountCouponIndex) => (
              <div className="flex flex-row items-end justify-end" key={discountCouponIndex}>
                <Text fontStyle="LL" className="pr-4 text-textDefault-body">
                  {word("box.member.table.offer.item.discount.label")}
                </Text>
                <Text fontStyle="BM" className="text-textDefault-heading">
                  {formatCurrency(
                    discountCoupon.couponType === CouponType.DIRECT
                      ? -discountCoupon.amount
                      : discountCoupon.amount < 100
                        ? -(discountCoupon.amount * transactionDetails.amount) / (100 - discountCoupon.amount)
                        : -transactionDetails.amount,
                    transactionDetails.currency,
                    language,
                  )}
                </Text>
              </div>
            ))}

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.excludingVATTotal")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {formatCurrency(
                (transactionDetails.amount || 0) - (transactionDetails.totalTaxAmount || 0),
                transactionDetails.currency,
                language,
              )}
            </Text>
          </div>

          {transactionDetails.totalTaxAmount > 0 && (
            <div className="flex flex-row items-end justify-end">
              <Text fontStyle="LL" className="pr-4 text-textDefault-body">
                {word("finances.array.invoice.VATTotal")}
              </Text>
              <Text fontStyle="BM" className="text-textDefault-heading">
                {formatCurrency(transactionDetails.totalTaxAmount || 0, transactionDetails.currency, language)}
              </Text>
            </div>
          )}

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.refund")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {formatCurrency(-transactionDetails.refundAmount || 0, transactionDetails.currency, language)}
            </Text>
          </div>

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.fee")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {formatCurrency(transactionDetails.fee || 0, transactionDetails.currency, language)}
            </Text>
          </div>

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.includingVATTotal")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {formatCurrency(
                transactionDetails.amount - transactionDetails.refundAmount - transactionDetails.fee || 0,
                transactionDetails.currency,
                language,
              )}
            </Text>
          </div>
        </div>
      )
    } else if (
      transactionDetails instanceof TransactionDetailsEntity &&
      transactionDetails.disputed &&
      !!transactionDetails.disputeDetail
    ) {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.dispute.paymentAmount")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {formatCurrency(transactionDetails.amount || 0, transactionDetails.currency, language)}
            </Text>
          </div>

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.dispute.disputedAmount")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {"- " + formatCurrency(transactionDetails.disputeDetail.amount, transactionDetails.currency, language)}
            </Text>
          </div>

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.dispute.fee")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {"- " + formatCurrency(transactionDetails.fee, transactionDetails.currency, language)}
            </Text>
          </div>

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.dispute.disputeFee")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {"- " + formatCurrency(transactionDetails.disputeDetail.fee, transactionDetails.currency, language)}
            </Text>
          </div>

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.dispute.total")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {"- " +
                formatCurrency(
                  transactionDetails.fee + transactionDetails.disputeDetail.fee,
                  transactionDetails.currency,
                  language,
                )}
            </Text>
          </div>
        </div>
      )
    } else {
      return (
        <div className="flex flex-col gap-2">
          {discountsPromoCode.length > 0 &&
            discountsPromoCode.map((promoCode, promoCodeIndex) => (
              <div className="flex flex-row items-end justify-end" key={promoCodeIndex}>
                <Text fontStyle="LL" className="pr-4 text-textDefault-body">
                  {promoCode.name ? word("promoCode.short", { code: shortenText(promoCode.name, 10) }) : ""}
                </Text>
                <Text fontStyle="BM" className="text-textDefault-heading">
                  {formatCurrency(
                    promoCode.couponType === CouponType.DIRECT
                      ? -promoCode.amount
                      : -(totalAmount * promoCode.amount) / 100,
                    transactionDetails.currency,
                    language,
                  )}
                </Text>
              </div>
            ))}

          {discountsCoupon.length > 0 &&
            discountsCoupon.map((discountCoupon, discountCouponIndex) => (
              <div className="flex flex-row items-end justify-end" key={discountCouponIndex}>
                <Text fontStyle="LL" className="pr-4 text-textDefault-body">
                  {word("box.member.table.offer.item.discount.label")}
                </Text>

                <Text fontStyle="BM" className="text-textDefault-heading">
                  {formatCurrency(
                    discountCoupon.couponType === CouponType.DIRECT
                      ? -discountCoupon.amount
                      : discountCoupon.amount < 100
                        ? -(discountCoupon.amount * transactionDetails.amount) / (100 - discountCoupon.amount)
                        : -transactionDetails.amount,
                    transactionDetails.currency,
                    language,
                  )}
                </Text>
              </div>
            ))}

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.includingVATTotal")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {formatCurrency(transactionDetails.amount || 0, transactionDetails.currency, language)}
            </Text>
          </div>

          {transactionDetails.totalTaxAmount > 0 && (
            <div className="flex flex-row items-end justify-end">
              <Text fontStyle="LL" className="pr-4 text-textDefault-body">
                {word("finances.array.invoice.VATTotal")}
              </Text>
              <Text fontStyle="BM" className="text-textDefault-heading">
                {formatCurrency(transactionDetails.totalTaxAmount || 0, transactionDetails.currency, language)}
              </Text>
            </div>
          )}
          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {word("finances.array.invoice.fees")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {transactionDetails.fee !== 0
                ? "- " + formatCurrency(transactionDetails.fee || 0, transactionDetails.currency, language)
                : "--"}
            </Text>
          </div>

          <div className="flex flex-row items-end justify-end">
            <Text fontStyle="LL" className="pr-4 text-textDefault-body">
              {transactionDetails.status === TransactionStatus.PENDING
                ? word("finances.array.invoice.dueAmount")
                : word("finances.array.invoice.receivedAmount")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-heading">
              {formatCurrency(transactionDetails.netAmount || 0, transactionDetails.currency, language)}
            </Text>
          </div>
        </div>
      )
    }
  }, [transactionDetails, className, props, language, totalAmount])

  const columns = [
    columnHelper.accessor("description", {
      header: word("box.form.description.label"),
      cell: ({ row: { original: data } }) => {
        if (data.periodStart || data.periodEnd) {
          const periodStart = dayjs(data.periodStart).format("L")
          const periodEnd = dayjs(data.periodEnd).format("L")
          const period =
            periodStart && periodEnd
              ? `${periodStart} - ${periodEnd}`
              : periodStart
                ? periodStart
                : periodEnd
                  ? periodEnd
                  : ""
          return <Text fontStyle="BS" className="text-textDefault-body">{`${data.description} (${period})`}</Text>
        }
        return <Text fontStyle="BS" className="text-textDefault-body">{`${data.description}`}</Text>
      },
      meta: {
        headerClassName: "w-3/7",
        className: "w-3/7",
      },
    }),
    columnHelper.accessor("netAmount", {
      header: word("finances.array.invoice.net_price"),
      cell: ({ row: { original: data } }) => (
        <Text fontStyle="BS" className="text-textDefault-body">
          {formatCurrency(data.netAmount, data.currency, language)}
        </Text>
      ),
      meta: {
        headerClassName: "w-1/7 p-0!",
        className: "w-1/7 p-0!",
      },
    }),
    columnHelper.accessor("quantity", {
      header: word("global.checkout.summary.quantity"),
      cell: ({ row: { original: data } }) => (
        <Text fontStyle="BS" className="text-textDefault-body">
          {data.quantity}
        </Text>
      ),
      meta: {
        headerClassName: "w-1/7 p-0!",
        className: "w-1/7",
      },
    }),
    ...(transactionDetails.lines.some((line) => line.taxAmount && line.taxAmount > 0)
      ? [
          columnHelper.accessor("taxPercent", {
            header: word("finances.array.invoice.taxe_price_percent"),
            cell: ({ row: { original: data } }) => (
              <Text fontStyle="BS" className="text-textDefault-body">
                {data.taxPercent}
              </Text>
            ),
            meta: {
              headerClassName: "w-1/7 p-0!",
              className: "w-1/7",
            },
          }),
        ]
      : []),
    columnHelper.accessor("totalAmount", {
      header: word("finances.array.invoice.total"),
      cell: ({ row: { original: data } }) => (
        <Text fontStyle="BS" className="text-textDefault-body">
          {formatCurrency(data.totalAmount, data.currency, language)}
        </Text>
      ),
      meta: {
        headerClassName: "w-1/7 p-0!",
        className: "w-1/7 p-0!",
      },
    }),
  ]

  return (
    <>
      <HUDatatable data={transactionDetails.lines ?? []} columns={columns} hideFooter className="*:table-fixed" />

      {footerGroup}
    </>
  )
}
